/**
 * Brand Social Video carousel + modal
 * - Desktop hover on card: muted preview play
 * - Click: open modal with video + product dock / side panel
 * - Close via X, backdrop, Escape
 */
(function () {
  var BODY_OPEN = 'bsv-modal-open';
  var instances = new WeakMap();

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function canHover() {
    return window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  }

  function isMobile() {
    return window.matchMedia && window.matchMedia('(max-width: 749px)').matches;
  }

  function clamp(n, min, max) {
    return Math.max(min, Math.min(max, n));
  }

  function safePlay(video) {
    if (!video) return Promise.resolve();
    var p = video.play();
    if (p && typeof p.catch === 'function') {
      return p.catch(function () {});
    }
    return Promise.resolve();
  }

  function BrandSocialVideo(root) {
    this.root = root;
    this.track = root.querySelector('[data-bsv-track]');
    this.viewport = root.querySelector('[data-bsv-viewport]');
    this.prevBtn = root.querySelector('[data-bsv-prev]');
    this.nextBtn = root.querySelector('[data-bsv-next]');
    this.dotsHost = root.querySelector('[data-bsv-dots]');
    this.modal = root.querySelector('[data-bsv-modal]');
    this.videoEl = root.querySelector('[data-bsv-video]');
    this.dialog = root.querySelector('[data-bsv-dialog]');
    this.mediaWrap = root.querySelector('.bsv-modal__media');
    this.enableModal = root.getAttribute('data-enable-modal') !== 'false';
    this.stepMode = root.getAttribute('data-step-desktop') || 'page';
    this.cards = [];
    this.dots = [];
    this.activeIndex = 0;
    this.scrollRaf = null;
    this.progressRaf = null;
    this.lastFocus = null;
    this.previewCard = null;
    this.controlsHideTimer = null;
    this.bound = {};

    if (!this.track) return;
    this.refreshCards();
    this.buildDots();
    this.bind();
    this.updateUi();
  }

  BrandSocialVideo.prototype.refreshCards = function () {
    this.cards = Array.prototype.slice.call(this.track.querySelectorAll('[data-bsv-card]'));
  };

  BrandSocialVideo.prototype.cardStep = function () {
    var card = this.cards[0];
    if (!card) return 240;
    var style = window.getComputedStyle(this.track);
    var gap = parseFloat(style.columnGap || style.gap) || 10;
    return card.getBoundingClientRect().width + gap;
  };

  BrandSocialVideo.prototype.visibleCount = function () {
    if (!this.viewport || !this.cards.length) return 1;
    if (isMobile()) return 1;
    var w = this.viewport.getBoundingClientRect().width;
    var step = this.cardStep();
    return Math.max(1, Math.floor((w + 1) / step));
  };

  BrandSocialVideo.prototype.moveStep = function () {
    if (isMobile()) return 1;
    if (this.stepMode === '1') return 1;
    return this.visibleCount();
  };

  BrandSocialVideo.prototype.maxIndex = function () {
    return Math.max(0, this.cards.length - this.visibleCount());
  };

  BrandSocialVideo.prototype.scrollToIndex = function (index, smooth) {
    if (!this.cards.length) return;
    index = clamp(index, 0, this.maxIndex());
    this.activeIndex = index;
    var card = this.cards[index];
    if (!card) return;

    var behavior = smooth && !prefersReducedMotion() ? 'smooth' : 'auto';

    if (isMobile()) {
      var trackRect = this.track.getBoundingClientRect();
      var cardRect = card.getBoundingClientRect();
      var delta = cardRect.left - trackRect.left - (trackRect.width - cardRect.width) / 2;
      this.track.scrollBy({ left: delta, behavior: behavior });
    } else {
      this.track.scrollTo({ left: card.offsetLeft, behavior: behavior });
    }

    this.updateUi();
  };

  BrandSocialVideo.prototype.indexFromScroll = function () {
    if (!this.cards.length) return 0;
    if (isMobile()) {
      var trackRect = this.track.getBoundingClientRect();
      var center = trackRect.left + trackRect.width / 2;
      var best = 0;
      var bestDist = Infinity;
      for (var i = 0; i < this.cards.length; i++) {
        var r = this.cards[i].getBoundingClientRect();
        var c = r.left + r.width / 2;
        var d = Math.abs(c - center);
        if (d < bestDist) {
          bestDist = d;
          best = i;
        }
      }
      return best;
    }

    var left = this.track.scrollLeft;
    var step = this.cardStep();
    return clamp(Math.round(left / step), 0, this.maxIndex());
  };

  BrandSocialVideo.prototype.buildDots = function () {
    if (!this.dotsHost) return;
    this.dotsHost.innerHTML = '';
    this.dots = [];
    var pages = isMobile() ? this.cards.length : Math.max(1, this.maxIndex() + 1);

    for (var i = 0; i < pages; i++) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'bsv__dot';
      btn.setAttribute('role', 'tab');
      btn.setAttribute('aria-label', 'Go to slide ' + (i + 1));
      btn.setAttribute('data-bsv-dot-index', String(i));
      this.dotsHost.appendChild(btn);
      this.dots.push(btn);
    }
  };

  BrandSocialVideo.prototype.updateUi = function () {
    var idx = this.activeIndex;
    var max = this.maxIndex();

    if (this.prevBtn) {
      var atStart = idx <= 0 && this.track.scrollLeft <= 2;
      this.prevBtn.disabled = atStart;
      this.prevBtn.setAttribute('aria-disabled', atStart ? 'true' : 'false');
    }
    if (this.nextBtn) {
      var atEnd = idx >= max && this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 2;
      this.nextBtn.disabled = atEnd;
      this.nextBtn.setAttribute('aria-disabled', atEnd ? 'true' : 'false');
    }

    for (var d = 0; d < this.dots.length; d++) {
      var active = d === idx;
      this.dots[d].classList.toggle('is-active', active);
      this.dots[d].setAttribute('aria-selected', active ? 'true' : 'false');
    }

    for (var c = 0; c < this.cards.length; c++) {
      var card = this.cards[c];
      var inRange = c >= idx && c < idx + this.visibleCount();
      if (isMobile()) inRange = c === idx;
      card.setAttribute('aria-hidden', inRange ? 'false' : 'true');
      var hit = card.querySelector('[data-bsv-open]');
      if (hit) hit.tabIndex = inRange ? 0 : -1;
    }
  };

  BrandSocialVideo.prototype.onScroll = function () {
    var self = this;
    if (this.scrollRaf) cancelAnimationFrame(this.scrollRaf);
    this.scrollRaf = requestAnimationFrame(function () {
      self.activeIndex = self.indexFromScroll();
      self.updateUi();
    });
  };

  BrandSocialVideo.prototype.stopAllPreviews = function () {
    var cards = this.cards;
    for (var i = 0; i < cards.length; i++) {
      cards[i].classList.remove('is-previewing');
      var preview = cards[i].querySelector('[data-bsv-preview]');
      if (preview) {
        preview.pause();
        preview.currentTime = 0;
      }
    }
    this.previewCard = null;
  };

  BrandSocialVideo.prototype.startPreview = function (card) {
    if (!canHover() || prefersReducedMotion() || !card) return;
    if (this.modal && !this.modal.hidden) return;

    var src = card.getAttribute('data-video-src') || '';
    var preview = card.querySelector('[data-bsv-preview]');
    if (!src || !preview) return;

    if (this.previewCard && this.previewCard !== card) {
      this.stopAllPreviews();
    }

    if (!preview.getAttribute('src')) {
      preview.src = src;
    }
    preview.muted = true;
    card.classList.add('is-previewing');
    this.previewCard = card;
    safePlay(preview);
  };

  BrandSocialVideo.prototype.stopPreview = function (card) {
    if (!card) return;
    card.classList.remove('is-previewing');
    var preview = card.querySelector('[data-bsv-preview]');
    if (preview) {
      preview.pause();
      preview.currentTime = 0;
    }
    if (this.previewCard === card) this.previewCard = null;
  };

  BrandSocialVideo.prototype.fillProduct = function (prefix, data) {
    var map = {
      title: '[data-bsv-modal-product-title' + prefix + ']',
      vendor: '[data-bsv-modal-product-vendor' + prefix + ']',
      price: '[data-bsv-modal-product-price' + prefix + ']',
      compare: '[data-bsv-modal-product-compare' + prefix + ']',
      img: '[data-bsv-modal-product-img' + prefix + ']',
      cta: '[data-bsv-modal-cta' + prefix + ']',
      wrap: '[data-bsv-modal-product' + (prefix === '-panel' ? '-panel' : '') + ']'
    };

    var wrap = this.modal.querySelector(map.wrap);
    if (!wrap) return;

    // Only show product UI when a real product (or titled link target) exists.
    var hasProduct = !!(data.productTitle || data.productImage);
    wrap.hidden = !hasProduct;
    if (!hasProduct) return;

    var title = this.modal.querySelector(map.title);
    var vendor = this.modal.querySelector(map.vendor);
    var price = this.modal.querySelector(map.price);
    var compare = this.modal.querySelector(map.compare);
    var img = this.modal.querySelector(map.img);
    var cta = this.modal.querySelector(map.cta);

    if (title) title.textContent = data.productTitle;
    if (vendor) vendor.textContent = data.vendor || '';
    if (price) {
      price.textContent = data.showPrice ? data.productPrice : '';
      price.hidden = !data.showPrice || !data.productPrice;
    }
    if (compare) {
      if (data.showPrice && data.productCompare) {
        compare.hidden = false;
        compare.textContent = data.productCompare;
      } else {
        compare.hidden = true;
        compare.textContent = '';
      }
    }
    if (img) {
      if (data.productImage) {
        img.hidden = false;
        img.src = data.productImage;
        img.alt = data.productTitle || '';
      } else {
        img.removeAttribute('src');
        img.hidden = true;
      }
    }
    if (cta) {
      var href = data.link || data.productUrl || '';
      if (href && href !== '#') {
        cta.hidden = false;
        cta.removeAttribute('aria-disabled');
        cta.href = href;
        cta.setAttribute('href', href);
      } else {
        cta.hidden = true;
        cta.href = '#';
        cta.setAttribute('aria-disabled', 'true');
      }
      cta.textContent = data.btnLabel || 'Shop';
    }
  };

  BrandSocialVideo.prototype.syncPlayIcons = function () {
    if (!this.modal || !this.videoEl) return;
    var paused = !!this.videoEl.paused;
    this.modal.classList.toggle('is-paused', paused);

    // Do not use SVG [hidden] — theme `[hidden]{display:none!important}` fights icon swap.
    // Visibility is 100% via `.is-paused` in CSS.
    var playIcon = this.modal.querySelector('.bsv-modal__icon-play');
    var pauseIcon = this.modal.querySelector('.bsv-modal__icon-pause');
    if (playIcon) {
      playIcon.removeAttribute('hidden');
      playIcon.setAttribute('aria-hidden', paused ? 'false' : 'true');
    }
    if (pauseIcon) {
      pauseIcon.removeAttribute('hidden');
      pauseIcon.setAttribute('aria-hidden', paused ? 'true' : 'false');
    }

    var toggleBtn = this.modal.querySelector('[data-bsv-toggle-play]');
    if (toggleBtn) {
      toggleBtn.setAttribute('aria-label', paused ? 'Play' : 'Pause');
    }
  };

  BrandSocialVideo.prototype.syncMuteIcons = function () {
    if (!this.modal || !this.videoEl) return;
    var muted = !!this.videoEl.muted;
    this.modal.classList.toggle('is-muted', muted);

    var unmuteIcon = this.modal.querySelector('.bsv-modal__icon-unmute');
    var muteIcon = this.modal.querySelector('.bsv-modal__icon-mute');
    if (unmuteIcon) {
      unmuteIcon.removeAttribute('hidden');
      unmuteIcon.setAttribute('aria-hidden', muted ? 'true' : 'false');
    }
    if (muteIcon) {
      muteIcon.removeAttribute('hidden');
      muteIcon.setAttribute('aria-hidden', muted ? 'false' : 'true');
    }

    var muteBtn = this.modal.querySelector('[data-bsv-mute]');
    if (muteBtn) {
      muteBtn.setAttribute('aria-label', muted ? 'Unmute' : 'Mute');
      muteBtn.setAttribute('aria-pressed', muted ? 'true' : 'false');
    }
  };

  BrandSocialVideo.prototype.updateProgress = function () {
    var self = this;
    if (!this.videoEl) return;
    var bar = this.modal && this.modal.querySelector('[data-bsv-progress]');
    if (!bar) return;

    var tick = function () {
      if (!self.videoEl || self.modal.hidden) return;
      var dur = self.videoEl.duration || 0;
      var cur = self.videoEl.currentTime || 0;
      var pct = dur ? (cur / dur) * 100 : 0;
      bar.style.width = pct + '%';
      self.progressRaf = requestAnimationFrame(tick);
    };

    if (this.progressRaf) cancelAnimationFrame(this.progressRaf);
    this.progressRaf = requestAnimationFrame(tick);
  };

  BrandSocialVideo.prototype.showControlsTemporarily = function () {
    if (!this.mediaWrap) return;
    var self = this;
    this.mediaWrap.classList.add('is-controls-visible');
    if (this.controlsHideTimer) clearTimeout(this.controlsHideTimer);
    this.controlsHideTimer = setTimeout(function () {
      if (!self.videoEl || self.videoEl.paused) return;
      self.mediaWrap.classList.remove('is-controls-visible');
    }, 2800);
  };

  BrandSocialVideo.prototype.openModal = function (card) {
    if (!this.enableModal || !this.modal || !card) return;

    this.stopAllPreviews();

    var data = {
      src: card.getAttribute('data-video-src') || '',
      caption: card.getAttribute('data-caption') || '',
      account: card.getAttribute('data-account') || '',
      badge: card.getAttribute('data-badge') || '',
      link: card.getAttribute('data-link') || '',
      btnLabel: card.getAttribute('data-btn-label') || 'Shop',
      showPrice: card.getAttribute('data-show-price') !== 'false',
      productTitle: card.getAttribute('data-product-title') || '',
      vendor: card.getAttribute('data-product-vendor') || '',
      productUrl: card.getAttribute('data-product-url') || '',
      productPrice: card.getAttribute('data-product-price') || '',
      productCompare: card.getAttribute('data-product-compare') || '',
      productImage: card.getAttribute('data-product-image') || ''
    };

    this.lastFocus = document.activeElement;

    var accountEl = this.modal.querySelector('[data-bsv-modal-account]');
    var captionEls = this.modal.querySelectorAll('[data-bsv-modal-caption], [data-bsv-modal-caption-visible]');
    var captionOverlay = this.modal.querySelector('[data-bsv-modal-caption-overlay]');
    var badgeEl = this.modal.querySelector('[data-bsv-modal-badge]');

    if (accountEl) accountEl.textContent = data.account;
    for (var ci = 0; ci < captionEls.length; ci++) {
      captionEls[ci].textContent = data.caption || 'Video';
    }
    if (captionOverlay) captionOverlay.textContent = data.caption;
    if (badgeEl) {
      if (data.badge) {
        badgeEl.hidden = false;
        badgeEl.textContent = data.badge;
      } else {
        badgeEl.hidden = true;
        badgeEl.textContent = '';
      }
    }

    this.fillProduct('', data);
    this.fillProduct('-panel', data);

    if (this.videoEl) {
      this.videoEl.pause();
      this.videoEl.removeAttribute('src');
      this.videoEl.load();
      if (data.src) {
        this.videoEl.src = data.src;
        this.videoEl.muted = true;
        var self = this;
        var afterPlay = function () {
          self.syncPlayIcons();
        };
        this.videoEl.addEventListener('playing', afterPlay, { once: true });
        safePlay(this.videoEl).then(function () {
          self.syncPlayIcons();
        });
        // Fallback if play() resolves without a playing event yet
        setTimeout(function () {
          self.syncPlayIcons();
        }, 120);
      }
    }

    this.modal.hidden = false;
    this.modal.setAttribute('aria-hidden', 'false');
    this.modal.classList.add('is-muted');
    document.body.classList.add(BODY_OPEN);
    this.syncPlayIcons();
    this.syncMuteIcons();
    this.updateProgress();
    this.showControlsTemporarily();
    if (this.dialog) this.dialog.focus();
  };

  BrandSocialVideo.prototype.closeModal = function () {
    if (!this.modal || this.modal.hidden) return;
    if (this.progressRaf) cancelAnimationFrame(this.progressRaf);
    if (this.controlsHideTimer) clearTimeout(this.controlsHideTimer);
    if (this.mediaWrap) this.mediaWrap.classList.remove('is-controls-visible');
    if (this.videoEl) {
      this.videoEl.pause();
      this.videoEl.removeAttribute('src');
      this.videoEl.load();
    }
    this.modal.hidden = true;
    this.modal.setAttribute('aria-hidden', 'true');
    this.modal.classList.remove('is-paused');
    this.modal.classList.remove('is-muted');
    document.body.classList.remove(BODY_OPEN);
    if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
      this.lastFocus.focus();
    }
  };

  BrandSocialVideo.prototype.bind = function () {
    var self = this;

    this.bound.onClick = function (event) {
      var openBtn = event.target.closest('[data-bsv-open]');
      if (openBtn && self.root.contains(openBtn)) {
        event.preventDefault();
        self.openModal(openBtn.closest('[data-bsv-card]'));
        return;
      }

      var closeBtn = event.target.closest('[data-bsv-close]');
      if (closeBtn && self.modal && self.modal.contains(closeBtn)) {
        event.preventDefault();
        self.closeModal();
        return;
      }

      if (self.modal && !self.modal.hidden) {
        if (event.target.closest('[data-bsv-toggle-play]')) {
          event.preventDefault();
          event.stopPropagation();
          if (!self.videoEl) return;
          if (self.videoEl.paused) {
            safePlay(self.videoEl).then(function () {
              self.syncPlayIcons();
            });
          } else {
            self.videoEl.pause();
            self.syncPlayIcons();
          }
          self.showControlsTemporarily();
          return;
        }
        var skip = event.target.closest('[data-bsv-skip]');
        if (skip) {
          event.preventDefault();
          event.stopPropagation();
          if (!self.videoEl) return;
          var amount = parseFloat(skip.getAttribute('data-bsv-skip')) || 0;
          self.videoEl.currentTime = Math.max(0, (self.videoEl.currentTime || 0) + amount);
          self.showControlsTemporarily();
          return;
        }
        if (event.target.closest('[data-bsv-mute]')) {
          event.preventDefault();
          event.stopPropagation();
          if (!self.videoEl) return;
          var nextMuted = !self.videoEl.muted;
          self.videoEl.muted = nextMuted;
          if (!nextMuted) {
            // Ensure audible after user gesture (autoplay starts muted).
            try {
              self.videoEl.volume = 1;
            } catch (e) {}
          }
          self.syncMuteIcons();
          self.showControlsTemporarily();
          return;
        }

        var shopLink = event.target.closest('.bsv-modal__cta, [data-bsv-modal-cta], [data-bsv-modal-cta-panel]');
        if (shopLink && self.modal.contains(shopLink)) {
          var href = shopLink.getAttribute('href') || '';
          if (!href || href === '#' || shopLink.getAttribute('aria-disabled') === 'true') {
            event.preventDefault();
            return;
          }
          // Let the browser navigate; stop other modal handlers from interfering.
          event.stopPropagation();
          return;
        }

        // Mobile has no hover: tap video area to reveal controls / toggle play.
        var onMedia = event.target.closest('.bsv-modal__media');
        var onUi = event.target.closest('button, a, [data-bsv-close], .bsv-modal__controls, .bsv-modal__side-tools');
        if (onMedia && !onUi && self.videoEl) {
          event.preventDefault();
          if (!canHover() || isMobile()) {
            var visible = self.mediaWrap && self.mediaWrap.classList.contains('is-controls-visible');
            if (!visible) {
              self.showControlsTemporarily();
            } else if (self.videoEl.paused) {
              safePlay(self.videoEl).then(function () {
                self.syncPlayIcons();
              });
              self.showControlsTemporarily();
            } else {
              self.videoEl.pause();
              self.syncPlayIcons();
              self.showControlsTemporarily();
            }
          } else {
            if (self.videoEl.paused) {
              safePlay(self.videoEl).then(function () {
                self.syncPlayIcons();
              });
            } else {
              self.videoEl.pause();
              self.syncPlayIcons();
            }
            self.showControlsTemporarily();
          }
          return;
        }
      }

      var dot = event.target.closest('[data-bsv-dot-index]');
      if (dot && self.root.contains(dot)) {
        event.preventDefault();
        var i = parseInt(dot.getAttribute('data-bsv-dot-index'), 10) || 0;
        self.scrollToIndex(i, true);
      }
    };

    this.bound.onPointerOver = function (event) {
      var card = event.target.closest('[data-bsv-card]');
      if (card && self.root.contains(card)) self.startPreview(card);
    };

    this.bound.onPointerOut = function (event) {
      var card = event.target.closest('[data-bsv-card]');
      if (!card || !self.root.contains(card)) return;
      var related = event.relatedTarget;
      if (related && card.contains(related)) return;
      self.stopPreview(card);
    };

    this.bound.onPrev = function () {
      self.scrollToIndex(self.activeIndex - self.moveStep(), true);
    };
    this.bound.onNext = function () {
      self.scrollToIndex(self.activeIndex + self.moveStep(), true);
    };
    this.bound.onScroll = function () {
      self.onScroll();
    };
    this.bound.onKeydown = function (event) {
      if (event.key === 'Escape') {
        self.closeModal();
        return;
      }

      if (!self.root.contains(document.activeElement) && (!self.modal || self.modal.hidden)) return;
      if (self.modal && !self.modal.hidden) return;

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        self.scrollToIndex(self.activeIndex - self.moveStep(), true);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        self.scrollToIndex(self.activeIndex + self.moveStep(), true);
      }
    };
    this.bound.onResize = function () {
      self.buildDots();
      self.activeIndex = self.indexFromScroll();
      self.updateUi();
    };
    this.bound.onVideoEvent = function () {
      self.syncPlayIcons();
    };

    this.root.addEventListener('click', this.bound.onClick);
    this.root.addEventListener('pointerover', this.bound.onPointerOver);
    this.root.addEventListener('pointerout', this.bound.onPointerOut);
    // Modal is rendered inside [data-bsv], so do NOT also bind click on modal
    // or play/pause will fire twice and appear broken.
    if (this.modal && this.videoEl) {
      this.videoEl.addEventListener('play', this.bound.onVideoEvent);
      this.videoEl.addEventListener('playing', this.bound.onVideoEvent);
      this.videoEl.addEventListener('pause', this.bound.onVideoEvent);
    }
    if (this.prevBtn) this.prevBtn.addEventListener('click', this.bound.onPrev);
    if (this.nextBtn) this.nextBtn.addEventListener('click', this.bound.onNext);
    this.track.addEventListener('scroll', this.bound.onScroll, { passive: true });
    document.addEventListener('keydown', this.bound.onKeydown);
    window.addEventListener('resize', this.bound.onResize, { passive: true });
  };

  BrandSocialVideo.prototype.destroy = function () {
    this.stopAllPreviews();
    if (this.controlsHideTimer) clearTimeout(this.controlsHideTimer);
    this.closeModal();
    if (this.bound.onClick) {
      this.root.removeEventListener('click', this.bound.onClick);
    }
    if (this.bound.onPointerOver) this.root.removeEventListener('pointerover', this.bound.onPointerOver);
    if (this.bound.onPointerOut) this.root.removeEventListener('pointerout', this.bound.onPointerOut);
    if (this.prevBtn && this.bound.onPrev) this.prevBtn.removeEventListener('click', this.bound.onPrev);
    if (this.nextBtn && this.bound.onNext) this.nextBtn.removeEventListener('click', this.bound.onNext);
    if (this.track && this.bound.onScroll) this.track.removeEventListener('scroll', this.bound.onScroll);
    if (this.videoEl && this.bound.onVideoEvent) {
      this.videoEl.removeEventListener('play', this.bound.onVideoEvent);
      this.videoEl.removeEventListener('playing', this.bound.onVideoEvent);
      this.videoEl.removeEventListener('pause', this.bound.onVideoEvent);
    }
    if (this.bound.onKeydown) document.removeEventListener('keydown', this.bound.onKeydown);
    if (this.bound.onResize) window.removeEventListener('resize', this.bound.onResize);
    instances.delete(this.root);
  };

  function initRoot(root) {
    if (!root || instances.has(root)) return;
    if (!root.querySelector('[data-bsv-track]')) return;
    var inst = new BrandSocialVideo(root);
    instances.set(root, inst);
  }

  function initAll() {
    document.querySelectorAll('[data-bsv]').forEach(initRoot);
  }

  function destroyIn(node) {
    if (!node) return;
    var root = node.matches && node.matches('[data-bsv]') ? node : node.querySelector && node.querySelector('[data-bsv]');
    if (root && instances.has(root)) {
      instances.get(root).destroy();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    destroyIn(event.target);
    if (event.target) {
      var root = event.target.querySelector('[data-bsv]');
      if (root) initRoot(root);
    }
  });

  document.addEventListener('shopify:section:unload', function (event) {
    destroyIn(event.target);
  });

  document.addEventListener('shopify:block:select', function (event) {
    var card = event.target && event.target.closest ? event.target.closest('[data-bsv-card]') : null;
    if (!card) return;
    var root = card.closest('[data-bsv]');
    if (!root || !instances.has(root)) return;
    var inst = instances.get(root);
    var idx = parseInt(card.getAttribute('data-index'), 10) || 0;
    inst.scrollToIndex(idx, true);
  });
})();
